import React, { useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import SectionCard from '../components/SectionCard.jsx';
import NestedSection from '../components/NestedSection.jsx';
import KPICard from '../components/KPICard.jsx';
import DataTable from '../components/DataTable.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import VarianceWaterfallChart from '../components/VarianceWaterfallChart.jsx';
import TornadoChart from '../components/TornadoChart.jsx';
import { shapeIncomeStatement, shapeBalanceSheet, shapeCashFlowStatement, shapeIncomeStatementMulti, shapeBalanceSheetMulti, shapeCashFlowStatementMulti } from '../components/FormalStatementTable.jsx';
import FormalStatementSection from '../components/FormalStatementSection.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import ComparativeTablesView from '../components/ComparativeTablesView.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import LedgerProjectionClarityPanel from '../components/LedgerProjectionClarityPanel.jsx';
import { FAMILY_AP, RECURRING, AR_RECURRING, MORTGAGES } from '../data.js';
import {
  fmtMoney, fmtMoneyShort, fmtPct, ymLabel, currentYM,
  totalAssetsLiquid, totalCreditCardDebt, totalCCCredits,
  totalLongTermLiabilities, totalEquity, totalSuspense,
  calcMonthlyMortgages, calcFVFMRR,
  calcMonthlyFamilyPoolAR,
  inflatedFamilyDirect, inflatedMarian,
  calcMonthlyAllRecurring, calcFreeCashFlow,
  buildForecast, buildCombinedTrajectory,
  buildContributionMargin, buildSensitivityTornado, backCastActuals,
  applyAdjustingEntries,
  buildIncomeStatement, buildCashFlowSnapshot,
  totalFixedAssetsNet, totalCCDebtFromBlocks, totalCCCreditsFromBlocks, totalLTLFromBlocks,
  buildThreeStatementForDirection, getTabDirection,
  aggregateByDensity, fmtPeriod, normalizePeriodScalar, getEffectiveHorizon,
  currentPeriodStateForRows,
  getModuleFeatures,
} from '../engine.js';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW_COMPACT = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mt-3 mb-0';
const DENSITY_CHIP = 'inline-flex border border-border rounded-sm overflow-hidden bg-surface';
const DENSITY_BTN = 'bg-transparent border-0 text-text-muted px-[9px] py-1 text-[0.72rem] font-[inherit] cursor-pointer border-r border-border last:border-r-0 hover:bg-surface-hover hover:text-text';
const DENSITY_BTN_ACTIVE = 'bg-surface-el text-accent-gold font-semibold';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const COMPACT_LIST = 'mt-[0.6rem] mb-0 pl-[1.1rem] text-text-muted text-[0.84rem] leading-[1.65]';
const DRIVER_ROW = 'grid grid-cols-[1fr_auto] gap-[0.6rem] items-center py-[0.35rem] border-b border-dashed border-border last:border-b-0 text-[0.85rem]';
const SYNTH_GRID = 'flex flex-col gap-2';
const SYNTH_ROW = 'grid grid-cols-[2fr_1fr_1fr_1fr] gap-[0.85rem] items-stretch px-[0.7rem] py-[0.65rem] bg-surface-el border border-border rounded-sm max-[720px]:grid-cols-2';
const SYNTH_ROW_LABEL = 'text-[0.85rem] text-text font-semibold self-center max-[720px]:col-span-2';
const SYNTH_ROW_CELL = 'flex flex-col gap-[0.1rem] pl-[0.7rem] border-l border-border';
const SYNTH_ROW_CELL_LABEL = 'text-[0.65rem] uppercase tracking-[0.06em] text-text-dim';

const RANGE_OPTS = [
  { id: 6,  label: '6 mo' },
  { id: 12, label: '12 mo' },
  { id: 24, label: '24 mo' },
  { id: 36, label: '36 mo' },
];

export default function SynthesisTab({ scenario, setScenario, onJump, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  /* v8 Path E — scroll-to-section when sidebar routes to a sub-item. */
  useEffect(() => {
    if (!activeSection || typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);
  /* v8 — horizon now tied to the global Period × Density picker in the
     ScenarioBar. Removed the local "12 / 24 / 36 mo" picker since it's
     redundant with the app-wide control. */
  const horizon = getEffectiveHorizon(scenario);
  const ym = currentYM();

  const adj = scenario.includeAdjustments !== false
    ? applyAdjustingEntries(scenario)
    : { byAccount: {}, totals: { is: 0, bs: 0 }, count: 0, entries: [] };
  const isExpenseAdj = adj.count > 0 ? -adj.totals.is : 0;
  const bsLiabAdj = Object.entries(adj.byAccount)
    .filter(([k]) => {
      const lower = k.toLowerCase();
      return lower.includes('payable') || lower.includes('liabilit') || lower.includes('accrued');
    })
    .reduce((s, [, v]) => s + v, 0);

  const actualsLiquid = totalAssetsLiquid();
  const actualsCC     = totalCreditCardDebt();
  const actualsFCF    = useMemo(() => {
    const back = backCastActuals(1);
    return back[0]?.netFCF ?? 0;
  }, []);
  const actualsRecurringRevenue = 0;

  const budgetFCF    = useMemo(() => calcFreeCashFlow(scenario, ym), [scenario, ym]);
  const budgetRecurringRevenue = scenario.fvfTargetMRR ?? 0;
  const budgetCC     = actualsCC;
  const budgetLiquid = actualsLiquid;

  const forecast = useMemo(() => buildForecast(scenario, horizon), [scenario, horizon]);
  const forecastEnd      = forecast[forecast.length - 1] || {};
  const forecastFCFAvg   = forecast.length ? forecast.reduce((s, r) => s + r.netFCF, 0) / forecast.length : 0;
  const forecastLiquid   = forecastEnd.liquidEnding ?? actualsLiquid;
  const forecastCC       = forecastEnd.ccDebtEnding ?? actualsCC;
  const forecastRecurringRevenue = forecastEnd.fvfMRR ?? 0;

  const wfBudget = useMemo(() => calcFreeCashFlow(scenario, ym) * horizon, [scenario, ym, horizon]);
  const wfActual = actualsFCF * horizon;
  const wfForecast = forecast.reduce((s, r) => s + r.netFCF, 0);

  const trajectory = useMemo(() => {
    /* v8 Path B — split the global horizon between past and forward so
       Synthesis lives up to its name. Forward gets the slack month when
       horizon is odd. */
    const fwd  = Math.ceil(horizon / 2);
    const back = Math.floor(horizon / 2);
    const raw = buildCombinedTrajectory(scenario, fwd, back);
    let lastActualIdx = -1;
    for (let i = 0; i < raw.length; i++) if (raw[i].segment === 'actual') lastActualIdx = i;
    const firstForecastIdx = lastActualIdx + 1;
    return raw.map((r, i) => {
      const isActual = r.segment === 'actual';
      const isFirstForecast = i === firstForecastIdx;
      return {
        ...r,
        liquidActual:    isActual || isFirstForecast ? r.liquidEnding : null,
        liquidForecast:  isActual ? null : r.liquidEnding,
        ccActual:        isActual || isFirstForecast ? r.ccDebtEnding : null,
        ccForecast:      isActual ? null : r.ccDebtEnding,
      };
    });
  }, [scenario, horizon]);

  const apRows = useMemo(() => {
    const rows = [
      { id: 'fam-direct', stream: 'Direct support channel', cadence: 'Monthly', amount: inflatedFamilyDirect(scenario, ym), source: 'SUPPORT_AP.direct' },
      { id: 'marian',     stream: 'Routed support channel (Net 15)',           cadence: 'Net 15 lumpy',  amount: inflatedMarian(scenario, ym), source: 'SUPPORT_AP.routed' },
      { id: 'term-debt',  stream: `Term debt (${MORTGAGES.length} loans)`,     cadence: 'Monthly', amount: calcMonthlyMortgages(), source: 'TERM_DEBT' },
      { id: 'subs',       stream: `Recurring subs (M+Y+Q · ${RECURRING.monthly.length + RECURRING.yearly.length + RECURRING.quarterly.length} lines)`, cadence: 'Mixed', amount: calcMonthlyAllRecurring(ym, scenario), source: 'RECURRING' },
    ];
    return rows.sort((a, b) => b.amount - a.amount);
  }, [scenario, ym]);
  const apTotal = apRows.reduce((s, r) => s + r.amount, 0);
  const apTop3  = apRows.slice(0, 3);

  const arRows = useMemo(() => {
    const rows = [];
    AR_RECURRING.educationFund.forEach((s, i) => {
      let amt = s.amount;
      if (s.contactId === 'demo-contact-3') {
        if (scenario.jpMode === 'historical')   amt = 5000;
        else if (scenario.jpMode === 'custom')  amt = scenario.jpAmount ?? s.amount;
      } else if (s.contactId === 'demo-contact-4') amt = scenario.jcAmount ?? s.amount;
      else if (s.contactId === 'demo-contact-5')   amt = scenario.jmAmount ?? s.amount;
      const expected = amt * (s.collectionRate ?? 1);
      rows.push({
        id: `edu-${i}`,
        stream: s.from,
        scheduled: amt,
        rate: s.collectionRate ?? 1,
        expected,
        source: 'CONTRIBUTION_AR',
      });
    });
    rows.push({
      id: 'fampool',
      stream: 'Pooled contribution',
      scheduled: calcMonthlyFamilyPoolAR(ym),
      rate: 1,
      expected: calcMonthlyFamilyPoolAR(ym),
      source: 'POOLED_CONTRIBUTION_AR',
    });
    rows.push({
      id: 'fvfmrr',
      stream: `Recurring revenue (${scenario.fvfMethod || 'triangulated'})`,
      scheduled: scenario.fvfTargetMRR ?? 0,
      rate: 1,
      expected: calcFVFMRR(scenario, ym),
      source: 'SUBSCRIPTION_DRIVERS',
    });
    return rows.sort((a, b) => b.expected - a.expected);
  }, [scenario, ym]);
  const arTotalExpected = arRows.reduce((s, r) => s + r.expected, 0);
  const arTop3 = arRows.slice(0, 3);

  const cmRows = useMemo(() => buildContributionMargin(scenario, ym), [scenario, ym]);

  const synPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const synDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const synHorizon = synPeriods * synDensity;
  /* v8 Path C — direction-dispatched. Synthesis defaults to 'current'
     (spanning) but user can override per-tab. */
  const synDirection = getTabDirection(scenario, 'synthesis');
  const synDirLabel = synDirection === 'past' ? 'trailing' : synDirection === 'future' ? 'leading' : 'spanning';
  const synProj = useMemo(() => {
    if (synPeriods <= 1) return null;
    const raw = buildThreeStatementForDirection(scenario, synDirection, synHorizon, synDensity);
    const forecast         = aggregateByDensity(raw.forecast,         synDensity, 'last');
    const incomeStatements = aggregateByDensity(raw.incomeStatements, synDensity, 'sum');
    const balanceSheets    = aggregateByDensity(raw.balanceSheets,    synDensity, 'last');
    const cashFlows        = aggregateByDensity(raw.cashFlows,        synDensity, 'sum');
    const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, synDensity));
    const currentState     = currentPeriodStateForRows(forecast, synDensity);
    const currentLabel     = currentState.label;
    return { forecast, incomeStatements, balanceSheets, cashFlows, periodLabels, currentLabel, currentState };
  }, [scenario, synPeriods, synDensity, synHorizon, synDirection]);
  const synLabels = synProj?.periodLabels;
  const synCurrentLabel = synProj?.currentLabel ?? fmtPeriod(currentYM(), synDensity);
  const synCurrentProof = synProj?.currentState ?? null;

  const tornadoRows = useMemo(
    () => buildSensitivityTornado(scenario, horizon),
    [scenario, horizon],
  );

  const moneyFmt = (v, row) => <MoneyCell value={v} decimals={2} scenario={scenario} row={row} />;
  const trajectoryTooltipFormatter = (v) => fmtMoney(v, { decimals: 0 });
  const trajectoryLabelFormatter = (label, payload) => {
    const seg = payload?.[0]?.payload?.segment;
    return `${label}${seg ? ` · ${seg}` : ''}`;
  };
  const transitionYM = trajectory.find(p => p.segment === 'forecast')?.ym;
  const isLedgerProjectionSection = activeSection === 'ledger-projection';

  if (activeSection === 'comparative') {
    return (
      <div className={TAB_CONTENT}>
        <SubViewBreadcrumb moduleId="synthesis" activeSection={activeSection} onBack={onBackToOverview} />
        <ComparativeTablesView scenario={scenario} moduleId="synthesis" />
      </div>
    );
  }

  const features = getModuleFeatures(scenario, 'synthesis');

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="synthesis" activeSection={activeSection} onBack={onBackToOverview} />
      <div className="inline-flex items-center gap-2 text-accent-gold text-[0.78rem] tracking-[0.08em] mb-[0.75rem]">
        <span className="text-[0.95rem]">⟡</span>
        <span className="uppercase">{isLedgerProjectionSection ? 'Synthesis — Ledger projection proof' : 'Synthesis — what is · what should be · what will be'}</span>
      </div>

      {show('ledger-projection') && (
        <LedgerProjectionClarityPanel />
      )}

      {show('overview') && (
        <ModuleSettingsPanel
          scenario={scenario}
          setScenario={setScenario}
          moduleId="synthesis"
          title="Synthesis · module settings"
          features={[
            { key: 'trajectory',       label: 'Trajectory chart',      hint: 'Actuals + forecast combined' },
            { key: 'variance',         label: 'Variance waterfall',    hint: 'Budgets vs Actuals breakdown' },
            { key: 'tornado',          label: 'Sensitivity tornado',   hint: 'Driver impact on target metric' },
            { key: 'contribution',     label: 'Contribution margin',   hint: 'Per-driver contribution stack' },
            { key: 'formalStatements', label: 'Formal statements',     hint: 'IS / BS / CF × horizon' },
          ]}
        />
      )}

      {show('overview') && (<>
      {/* v8 — Horizon is now the global Period × Density picker in the
          model context bar. Small helper card left behind for context. */}
      <SectionCard
        title="Horizon"
        subtitle="Set in the model context bar's Periods × Density picker. Drives every forecast section below."
      >
        <p className={HINT}>
          Active: forward <strong className="text-text">{horizon} months</strong> · back-cast <strong className="text-text">3 months</strong> (synthetic-derived until contract source is verified).
        </p>
      </SectionCard>

      {/* Three-column KPI strip */}
      <SectionCard
        title="Actuals · Budgets · Forecasts — at a glance"
        subtitle="Current snapshot vs scenario plans vs forward projections. Each row is one metric, three lenses."
      >
        <div className={SYNTH_GRID}>
          <SynthRow
            label="Net cash flow (per month)"
            actuals={actualsFCF}     budget={budgetFCF}     forecast={forecastFCFAvg}
            tone={(v) => v >= 0 ? 'good' : 'bad'}
            sub={{ actuals: 'back-cast', budget: 'current month', forecast: `${horizon}mo avg` }}
            term="freeCashFlow" registryKey="freeCashFlow" onJump={onJump}
          />
          <SynthRow
            label="Liquid (cash on hand)"
            actuals={actualsLiquid}  budget={budgetLiquid}  forecast={forecastLiquid}
            tone={(v) => v >= 0 ? 'good' : 'bad'}
            sub={{ actuals: 'snapshot', budget: 'snapshot', forecast: `M+${horizon}` }}
            term="liquidAssets" registryKey="liquidAssets" onJump={onJump}
          />
          <SynthRow
            label="Short-term debt (outstanding)"
            actuals={actualsCC}      budget={budgetCC}      forecast={forecastCC}
            tone={(v) => v <= actualsCC ? 'good' : 'bad'}
            sub={{ actuals: 'snapshot', budget: 'snapshot', forecast: `M+${horizon}` }}
            term="ccDebt" registryKey="ccDebt" onJump={onJump}
          />
          <SynthRow
            label="Recurring revenue"
            actuals={actualsRecurringRevenue}     budget={budgetRecurringRevenue}     forecast={forecastRecurringRevenue}
            tone={(v) => v > 0 ? 'good' : undefined}
            sub={{ actuals: 'not active', budget: `target ${fmtMoney(scenario.fvfTargetMRR ?? 0, {decimals:0})}`, forecast: `M+${horizon}` }}
            term="MRR" registryKey="fvfMRR" onJump={onJump}
          />
        </div>
      </SectionCard>

      {adj.count > 0 && (
        <SectionCard
          title={`Adjusting Entries — ${adj.count} posted`}
          subtitle={`IS expense impact: ${isExpenseAdj >= 0 ? '+' : ''}${fmtMoney(isExpenseAdj, { decimals: 0 })} · BS liability impact: ${bsLiabAdj >= 0 ? '+' : ''}${fmtMoney(bsLiabAdj, { decimals: 0 })}`}
        >
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Posted entries" value={`${adj.count}`} sub="status: posted" provenance="ledger" provenanceTip="Count of adjusting JEs marked posted. These flow into every downstream statement." />
            <KPICard label="IS expense adj." value={fmtMoney(isExpenseAdj, { decimals: 0 })} tone={isExpenseAdj > 0 ? 'warn' : 'good'} sub="accrued expense not yet in model" provenance="computed" provenanceTip="Net effect of adjusting entries on the income statement (accrued/deferred expense movement)." />
            <KPICard label="BS liability adj." value={fmtMoney(bsLiabAdj, { decimals: 0 })} tone={bsLiabAdj > 0 ? 'warn' : 'good'} sub="accrued liabilities" provenance="computed" provenanceTip="Net liability-side movement from adjusting JEs — accrued AP, deferred revenue, etc." />
          </div>
        </SectionCard>
      )}
      </>)}

      {/* Formal financial statements */}
      {show('overview', 'formal') && features.formalStatements && (() => {
        const synIS = buildIncomeStatement(scenario);
        return (
          <>
            <FormalStatementSection
              tabId="synthesis" stmtKey="is"
              label="Income Statement (formal)"
              subtitle={synPeriods > 1
                ? `${synPeriods}-period ${synDirLabel} · A/B/F unifier (synthetic-derived history until contract source is verified)`
                : `Accrual basis · Net income: ${fmtMoney(synIS.netIncome, { decimals: 0 })}`}
              title="Statement of Income — Accrual Basis"
              entity="Selected reporting scope"
              period={synPeriods > 1 ? `${synLabels[0]} – ${synLabels[synLabels.length - 1]}` : `For the month ending ${ym}-30`}
              periods={synPeriods > 1 ? synLabels : undefined}
             sections={synPeriods > 1 ? shapeIncomeStatementMulti(synProj.incomeStatements) : shapeIncomeStatement(synIS)}
             currentPeriodLabel={synCurrentLabel}
             currentPeriodProof={synCurrentProof}
             scenario={scenario} setScenario={setScenario}
            />
            <FormalStatementSection
              tabId="synthesis" stmtKey="bs"
              label="Balance Sheet (formal)"
              subtitle={synPeriods > 1
                ? `${synPeriods}-period ${synDirLabel} BS · A = L + E (A/B/F unifier)`
                : `A = L + E · Assets ${fmtMoney(totalAssetsLiquid() + totalFixedAssetsNet(), { decimals: 0 })} · Liab ${fmtMoney(totalCCDebtFromBlocks() - totalCCCreditsFromBlocks() + totalLTLFromBlocks(), { decimals: 0 })}`}
              title="Statement of Financial Position"
              entity="Selected reporting scope"
              period={synPeriods > 1 ? `${synLabels[0]} – ${synLabels[synLabels.length - 1]}` : `As of ${ym}-30`}
              periods={synPeriods > 1 ? synLabels : undefined}
              sections={synPeriods > 1
                ? shapeBalanceSheetMulti(synProj.balanceSheets)
                : shapeBalanceSheet({
                    assetsLiquid:    totalAssetsLiquid(),
                    fixedAssetsNet:  totalFixedAssetsNet(),
                    ccDebt:          totalCCDebtFromBlocks(),
                    ccCredits:       totalCCCreditsFromBlocks(),
                    ltl:             totalLTLFromBlocks(),
                    eq:              totalEquity(),
                    susTotal:        totalSuspense(),
                  })}
             currentPeriodLabel={synCurrentLabel}
             currentPeriodProof={synCurrentProof}
             scenario={scenario} setScenario={setScenario}
            />
            <FormalStatementSection
              tabId="synthesis" stmtKey="cf"
              label="Cash Flow Statement (formal)"
              subtitle={synPeriods > 1
                ? `${synPeriods}-period ${synDirLabel} CF · CFO + CFI + CFF (A/B/F unifier)`
                : 'CFO + CFI + CFF = Net change in cash · single-month snapshot'}
              title="Statement of Cash Flows — Indirect Method"
              entity="Selected reporting scope"
              period={synPeriods > 1 ? `${synLabels[0]} – ${synLabels[synLabels.length - 1]}` : `For the month ending ${ym}-30`}
              periods={synPeriods > 1 ? synLabels : undefined}
              sections={synPeriods > 1
                ? shapeCashFlowStatementMulti(synProj.cashFlows)
                : shapeCashFlowStatement(buildCashFlowSnapshot(scenario, ym))}
             currentPeriodLabel={synCurrentLabel}
             currentPeriodProof={synCurrentProof}
             scenario={scenario} setScenario={setScenario}
            />
          </>
        );
      })()}

      {/* Variance waterfall */}
      {show('overview', 'variance') && features.variance && (
      <SectionCard
        sectionId="variance"
        title="Variance walk — Budgets → Actuals → Forecasts"
        subtitle={`Cumulative net FCF over ${horizon} months. Green = over plan, red = under.`}
      >
        <VarianceWaterfallChart
          budget={wfBudget}
          actual={wfActual}
          forecast={wfForecast}
          label={`${horizon}mo cumulative net FCF`}
        />
      </SectionCard>
      )}

      {/* Combined trajectory */}
      {show('overview', 'trajectory') && features.trajectory && (
      <SectionCard
        sectionId="trajectory"
        title="Combined trajectory — actuals into forecast"
        subtitle="Solid line = back-cast. Dashed line = forward forecast. Vertical marker = transition."
      >
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={trajectory} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--grid)" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} stroke="var(--border)" />
            <YAxis tickFormatter={fmtMoneyShort} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} stroke="var(--border)" />
            <Tooltip
              contentStyle={{
                background: 'rgba(26,30,39,0.97)',
                border: '1px solid #3a4050',
                borderRadius: 6,
                color: '#e7ecf3',
                fontSize: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)',
                backdropFilter: 'blur(6px)',
              }}
              labelStyle={{ color: '#8b93a7', fontWeight: 600, marginBottom: 4 }}
              itemStyle={{ color: '#e7ecf3' }}
              cursor={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 1 }}
              formatter={trajectoryTooltipFormatter}
              labelFormatter={trajectoryLabelFormatter}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
            <ReferenceLine y={0} stroke="var(--border-strong)" strokeDasharray="3 3" />
            {transitionYM && (
              <ReferenceLine
                x={ymLabel(transitionYM)}
                stroke="var(--accent-gold)"
                strokeDasharray="2 4"
                label={{ value: 'now', position: 'top', fill: 'var(--accent-gold)', fontSize: 11 }}
              />
            )}
            <Line type="monotone" dataKey="liquidActual"   name="Liquid (actual)"
              stroke="#4ade80" strokeWidth={2} dot={{ r: 3, fill: '#4ade80' }} connectNulls={false} isAnimationActive={true} animationDuration={400} animationEasing="ease-out" />
            <Line type="monotone" dataKey="liquidForecast" name="Liquid (forecast)"
              stroke="#4ade80" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={true} animationDuration={400} animationEasing="ease-out" />
            <Line type="monotone" dataKey="ccActual"   name="Short-term debt (actual)"
              stroke="#f87171" strokeWidth={2} dot={{ r: 3, fill: '#f87171' }} connectNulls={false} isAnimationActive={true} animationDuration={400} animationEasing="ease-out" />
            <Line type="monotone" dataKey="ccForecast" name="Short-term debt (forecast)"
              stroke="#f87171" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={true} animationDuration={400} animationEasing="ease-out" />
            <Line type="monotone" dataKey="fvfMRR" name="Recurring revenue"
              stroke="#fbbf24" strokeWidth={2} strokeDasharray="2 2" dot={false} isAnimationActive={true} animationDuration={400} animationEasing="ease-out" />
          </LineChart>
        </ResponsiveContainer>
      </SectionCard>
      )}

      {/* Unified AP cadence */}
      {show('overview') && (
      <NestedSection
        sectionId="syn-ap"
        title="Unified AP cadence"
        subtitle={`All outflow streams in one drawer · total ${fmtMoney(apTotal, { decimals: 0 })}/mo`}
        scenario={scenario} setScenario={setScenario}
        headline={
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Total monthly AP" value={<MoneyCell value={apTotal} decimals={2} tone="warn" term="monthlyBurn" registryKey="apTotal" onJump={onJump} label="Total monthly AP (all streams)" />} sub={`${apRows.length} streams`} provenance="computed" provenanceTip="Sum of every monthly AP stream — recurring obligations, support channels, term debt, and ad-hoc payables. Cross-module composition." />
            <KPICard label="Largest channel" value={<MoneyCell value={apRows[0]?.amount ?? 0} decimals={2} registryKey="apTotal" onJump={onJump} label={`Largest AP — ${apRows[0]?.stream}`} />} sub={apRows[0]?.stream} provenance="computed" provenanceTip="Largest single AP channel by monthly amount — auto-ranked across every stream." />
          </div>
        }
        topDrivers={
          <div>
            {apTop3.map((r) => (
              <div key={r.id} className={DRIVER_ROW}>
                <div>
                  <span className="text-text">{r.stream}</span>
                  <span className="text-text-dim text-[0.75rem]"> · {r.cadence}</span>
                </div>
                <span className="text-text tabular-nums font-semibold">{fmtMoney(r.amount, { decimals: 0 })}</span>
              </div>
            ))}
          </div>
        }
        fullCount={apRows.length}
        fullList={
          <DataTable
            compact
            columns={[
              { key: 'stream',  label: 'AP stream', width: '46%' },
              { key: 'cadence', label: 'Cadence',  width: '18%' },
              { key: 'amount',  label: 'Monthly',  align: 'right', width: '20%', format: moneyFmt },
              { key: 'source',  label: 'Source' },
            ]}
            rows={apRows}
            footer={{ stream: 'Total monthly AP', cadence: '', amount: apTotal, source: '' }}
          />
        }
      />
      )}

      {/* Unified AR rates */}
      {show('overview') && (
      <NestedSection
        sectionId="syn-ar"
        title="Unified AR rates"
        subtitle={`All inflow streams (excl. primary income) · total expected ${fmtMoney(arTotalExpected, { decimals: 0 })}/mo`}
        scenario={scenario} setScenario={setScenario}
        headline={
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Total expected AR" value={<MoneyCell value={arTotalExpected} decimals={2} tone="good" term="monthlyCashIn" registryKey="arTotal" onJump={onJump} label="Total expected AR (all streams)" />} sub={`${arRows.length} streams`} provenance="computed" provenanceTip="Sum of expected AR across every inflow stream: contributions, pooled contributions, subscription units, and custody fees. Rate-adjusted." />
            <KPICard label="Largest stream" value={<MoneyCell value={arRows[0]?.expected ?? 0} decimals={2} registryKey="arTotal" onJump={onJump} label={`Largest AR — ${arRows[0]?.stream}`} />} sub={arRows[0]?.stream} provenance="computed" provenanceTip="Largest single AR stream by expected monthly inflow — auto-ranked." />
          </div>
        }
        topDrivers={
          <div>
            {arTop3.map((r) => (
              <div key={r.id} className={DRIVER_ROW}>
                <div>
                  <span className="text-text">{r.stream}</span>
                  <span className="text-text-dim text-[0.75rem]"> · {fmtPct(r.rate)} rate</span>
                </div>
                <span className="text-text tabular-nums font-semibold">{fmtMoney(r.expected, { decimals: 0 })}</span>
              </div>
            ))}
          </div>
        }
        fullCount={arRows.length}
        fullList={
          <DataTable
            compact
            columns={[
              { key: 'stream',    label: 'AR stream', width: '40%' },
              { key: 'scheduled', label: 'Scheduled', align: 'right', width: '16%', format: moneyFmt },
              { key: 'rate',      label: 'Rate',      align: 'right', width: '12%', format: (v) => fmtPct(v) },
              { key: 'expected',  label: 'Expected',  align: 'right', width: '16%', format: moneyFmt },
              { key: 'source',    label: 'Source' },
            ]}
            rows={arRows}
            footer={{
              stream: 'Total',
              scheduled: arRows.reduce((s, r) => s + r.scheduled, 0),
              rate: '',
              expected: arTotalExpected,
              source: '',
            }}
          />
        }
      />
      )}

      {/* Contribution margin */}
      {show('overview', 'contribution') && features.contribution && (
      <SectionCard
        sectionId="contribution"
        title="Contribution margin — by revenue stream"
        subtitle="Master's-level Budgets read: each stream's gross, the direct cost it requires to exist, and the margin."
      >
        <DataTable
          columns={[
            { key: 'stream',      label: 'Revenue stream', width: '34%' },
            { key: 'gross',       label: 'Gross',        align: 'right', width: '14%', format: moneyFmt },
            { key: 'directCosts', label: 'Direct costs', align: 'right', width: '14%', format: moneyFmt },
            { key: 'marginAbs',   label: 'Margin',     align: 'right', width: '14%',
              format: (v, row) => <MoneyCell value={v} decimals={2} tone={v >= 0 ? 'good' : 'bad'} scenario={scenario} row={row} label={`${row.stream} — margin`} />
            },
            { key: 'marginPct',   label: 'Margin %',     align: 'right', width: '10%',
              format: (v) => v == null ? '—' : <span className={v >= 0 ? 'text-accent-green tabular-nums' : 'text-accent-red tabular-nums'}>{fmtPct(v)}</span>
            },
            { key: 'note',        label: 'Note' },
          ]}
          rows={cmRows.map((r, i) => ({ ...r, id: r.stream || i }))}
          footer={{
            stream: 'Total contribution',
            gross: cmRows.reduce((s, r) => s + r.gross, 0),
            directCosts: cmRows.reduce((s, r) => s + r.directCosts, 0),
            marginAbs: cmRows.reduce((s, r) => s + r.marginAbs, 0),
            marginPct: '',
            note: '',
          }}
        />
        <p className={HINT}>
          Direct costs in the operator-finance frame = the levies, channels, or offsets that exist
          <em className="not-italic"> only because </em> the revenue stream exists. Cuts through pure top-line growth illusion.
        </p>
      </SectionCard>
      )}

      {/* Sensitivity tornado */}
      {show('overview', 'tornado') && features.tornado && (
      <SectionCard
        sectionId="tornado"
        title="Sensitivity tornado — what moves the trajectory most"
        subtitle={`Each driver perturbed ±20%. Y-axis ranked by absolute ${horizon}mo net FCF impact.`}
      >
        <TornadoChart rows={tornadoRows} label={`${horizon}mo net FCF impact`} />
        <ul className={COMPACT_LIST}>
          {tornadoRows.map((r) => (
            <li key={r.key}>
              <strong className="text-text">{r.label}</strong> — +20% → {fmtMoney(r.plusDelta, { decimals: 0 })} · −20% → {fmtMoney(r.minusDelta, { decimals: 0 })} <em className="not-italic text-text-muted">· {r.hint}</em>
            </li>
          ))}
        </ul>
      </SectionCard>
      )}
    </div>
  );
}

/* A single Actuals/Budgets/Forecasts row */
function SynthRow({ label, actuals, budget, forecast, tone, sub = {}, term, registryKey, onJump }) {
  const toneFor = (v) => (tone ? tone(v) : undefined);
  return (
    <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-[0.85rem] items-stretch px-[0.7rem] py-[0.65rem] bg-surface-el border border-border rounded-sm max-[720px]:grid-cols-2">
      <div className="text-[0.85rem] text-text font-semibold self-center max-[720px]:col-span-2">{label}</div>
      <div className="flex flex-col gap-[0.1rem] pl-[0.7rem] border-l border-border">
        <span className="text-[0.65rem] uppercase tracking-[0.06em] text-text-dim">Actuals</span>
        <MoneyCell value={actuals} decimals={2} tone={toneFor(actuals)} term={term} registryKey={registryKey} onJump={onJump} label={`${label} — actuals`} />
        {sub.actuals && <em className="text-[0.7rem] text-text-muted not-italic">{sub.actuals}</em>}
      </div>
      <div className="flex flex-col gap-[0.1rem] pl-[0.7rem] border-l border-border">
        <span className="text-[0.65rem] uppercase tracking-[0.06em] text-text-dim">Budgets</span>
        <MoneyCell value={budget} decimals={2} tone={toneFor(budget)} term={term} registryKey={registryKey} onJump={onJump} label={`${label} — budget`} />
        {sub.budget && <em className="text-[0.7rem] text-text-muted not-italic">{sub.budget}</em>}
      </div>
      <div className="flex flex-col gap-[0.1rem] pl-[0.7rem] border-l border-border">
        <span className="text-[0.65rem] uppercase tracking-[0.06em] text-text-dim">Forecasts</span>
        <MoneyCell value={forecast} decimals={2} tone={toneFor(forecast)} term={term} registryKey={registryKey} onJump={onJump} label={`${label} — forecast`} />
        {sub.forecast && <em className="text-[0.7rem] text-text-muted not-italic">{sub.forecast}</em>}
      </div>
    </div>
  );
}
